import React from 'react';

import { useLocation } from 'react-router-dom';

import { Box, Container, Tab, Tabs } from '@mui/material';

import AttractionsPage from '~/components/Attractions/AttractionsPage';

import { ROLE_ADMIN_USER, ROLE_EXPERIENCES_COORDINATOR } from '~/consts/roles';

import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentUser from '~/hooks/useCurrentUser';

import canShowTab from '~/utils/canShowTab';

import CarouselMetricsTab from './Content/CarouselMetrics/CarouselMetricsTab';
import Collections from './Content/Collections/Collections';
import ContentTagsTab from './Content/ContentTagPerformance/ContentTagsTab';
import RegionCuration from './Content/RegionCuration/RegionCuration';
import SyncLogs from './Content/SyncLogs/SyncLogs';
import SyncOffers from './Content/SyncOffers/SyncOffers';
import { ExperiencesContent } from './ExperiencesContent';

export type FieldValueParams = {
  key: string;
  fieldValue: string;
  range?: 'min' | 'max';
};

export type Toggle = {
  [key: string]: boolean;
};

type CurationTab =
  | 'region-curation'
  | 'experiences-curation'
  | 'sync-offers'
  | 'collections'
  | 'carousel-metrics'
  | 'content-tag-performance'
  | 'sync-logs'
  | 'attractions';

const VALID_TABS: Array<CurationTab> = [
  'experiences-curation',
  'region-curation',
  'sync-offers',
  'collections',
  'sync-logs',
  'carousel-metrics',
  'content-tag-performance',
  'attractions',
];

export default function Experiences() {
  const { replace } = useAppNavigation();
  const location = useLocation();
  const { user } = useCurrentUser();
  const showCarouselMetrics = canShowTab(user, [ROLE_ADMIN_USER, ROLE_EXPERIENCES_COORDINATOR]);
  const showContentTagPerformance = canShowTab(user, [ROLE_ADMIN_USER, ROLE_EXPERIENCES_COORDINATOR]);

  const params = new URLSearchParams(location.search);
  const tabParam = params.get('tab') as CurationTab | null;
  const tab: CurationTab = tabParam && VALID_TABS.includes(tabParam) ? tabParam : 'experiences-curation';

  const handleTabChange = (_: React.SyntheticEvent, value: CurationTab) => {
    if (value === 'experiences-curation') {
      replace('/experiences');
    } else {
      replace(`/experiences?tab=${value}`);
    }
  };

  return (
    <Container maxWidth="xl">
      <Tabs sx={{ mb: 2 }} value={tab} onChange={handleTabChange} id="experience-tabs">
        <Tab value="experiences-curation" label="Experiences Curation" />
        <Tab value="region-curation" label="In Flow Region Curation" />
        <Tab value="sync-offers" label="Sync Offers" />
        <Tab value="collections" label="Collections" />
        <Tab value="sync-logs" label="Sync Logs" />
        {showCarouselMetrics && <Tab value="carousel-metrics" label="Carousel Metrics" />}
        {showContentTagPerformance && <Tab value="content-tag-performance" label="Content Tag" />}
        <Tab value="attractions" label="Attractions" />
      </Tabs>

      <Box mt={4}>
        {tab === 'experiences-curation' && <ExperiencesContent />}
        {tab === 'region-curation' && <RegionCuration />}
        {tab === 'sync-offers' && <SyncOffers />}
        {tab === 'collections' && <Collections />}
        {tab === 'sync-logs' && <SyncLogs />}
        {tab === 'carousel-metrics' && showCarouselMetrics && <CarouselMetricsTab />}
        {tab === 'content-tag-performance' && showContentTagPerformance && <ContentTagsTab />}
        {tab === 'attractions' && <AttractionsPage />}
      </Box>
    </Container>
  );
}
